import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Calendar, Timer, CheckCircle2 } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faClock,
  faHourglassHalf,
  faXmark,
  faCheck,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import defaultAvatar from "../../../assets/images/avatar/employee-1.png";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";

import {
  getPendingOvertimes,
  approveOvertime,
  rejectOvertime,
  deleteOvertime,
} from "../../../api/hrm/employees/overtimes";
import { formatDateVN } from "../../../utils/formatUtils";

const PendingOvertimeList = ({
  filters,
  refreshFlag,
  onChanged,
  autoOpenId = null,
  onAutoOpened,
}) => {
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const autoOpenedRef = useRef(null);

  const storedUser = JSON.parse(localStorage.getItem("user"));
  const currentUserId = storedUser?.id;

  // 🟢 Tự load dữ liệu tại đây
  const loadPending = async () => {
    try {
      setLoading(true);
      const res = await getPendingOvertimes(filters);
      setData(res || []);
    } catch (err) {
      console.error("❌ Lỗi load pending:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filters?.from_date || !filters?.to_date) return;
    loadPending();
  }, [filters, refreshFlag]);

  useEffect(() => {
    setRejectReason("");
    setRejectLoading(false);
  }, [selected]);

  useEffect(() => {
    if (!autoOpenId || autoOpenedRef.current === autoOpenId) return;
    const target = data.find((item) => String(item.id) === String(autoOpenId));
    if (!target) return;

    autoOpenedRef.current = autoOpenId;
    setSelected(target);
    onAutoOpened?.(target);
  }, [autoOpenId, data]);

  const handleToggleSelect = (item) => {
    if (selected?.id === item.id) {
      setSelected(null); // đóng popup
    } else {
      setSelected(item); // mở popup card khác
    }
  };

  // 🔥 DELETE OVERTIME
  const handleDelete = async (id, workDate) => {
    showConfirmDialog({
      title: "Xác nhận",
      message: `Bạn có chắc muốn xóa phiếu OT ngày ${formatDateVN(workDate)}?`,
      confirmText: "Xóa",
      cancelText: "Hủy",
    }).then(async () => {
      try {
        await deleteOvertime(id);

        // 🔥 ĐÓNG POPUP nếu đang mở đúng card bị xóa
        if (selected?.id === id) {
          setSelected(null);
        }

        showSuccessToast("Đã xóa phiếu đăng ký OT");

        // 🔥 Gọi cha để refresh UI
        onChanged?.();
      } catch (err) {
        showErrorToast("Không thể xóa phiếu");
      }
    });
  };

  // 🟢 Approve
  const doApprove = async () => {
    try {
      await approveOvertime(selected.id, { approver_id: currentUserId });
      setSelected(null);
      loadPending();
      onChanged?.();
    } catch (err) {
      console.error("Lỗi approve:", err);
    }
  };

  // 🟢 Reject
  const confirmReject = async () => {
    try {
      await rejectOvertime(selected.id, currentUserId, rejectReason);
      setShowRejectPopup(false);
      setSelected(null);
      loadPending();
      onChanged?.();
    } catch (err) {
      console.error("Lỗi reject:", err);
    }
  };

  return (
    <div className="mb-6">
      {data.length > 0 && (
        <h2 className="font-semibold text-base text-gray-700 mb-3 ml-2">
          Phiếu đăng ký chờ duyệt ({data.length} phiếu)
        </h2>
      )}

      {/* Loading */}
      {loading && (
        <p className="text-gray-600 italic ml-2">Đang tải dữ liệu...</p>
      )}

      {/* ==================== CARD LIST ==================== */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"> */}
      <div className="grid grid-cols-1 mb:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {data.map((item) => {
          const canApprove = item.next_approver_id === currentUserId;
          const hours = (item.ot_minutes / 60).toFixed(1);
          const avatar = item.employee_avatar || defaultAvatar;

          return (
            <div
              key={item.id}
              onClick={() => handleToggleSelect(item)}
              className="group relative bg-white rounded-[1.8rem] p-4 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
            >
              {/* ✅ Tag trạng thái thu gọn */}
              {canApprove ? (
                <div className="absolute top-0 right-0 bg-red-600 text-white px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-bl-xl shadow-sm">
                  Cần duyệt
                </div>
              ) : (
                item.employee_id === currentUserId && (
                  <div className="absolute top-0 right-0 bg-slate-100 text-slate-400 px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-bl-xl">
                    Chờ duyệt
                  </div>
                )
              )}

              {/* ✅ Header: Avatar + Info thu nhỏ khoảng cách */}
              <div className="flex gap-3 items-center mb-3">
                <div className="relative shrink-0">
                  <img
                    src={avatar}
                    className="w-10 h-10 rounded-xl object-cover border border-gray-50 shadow-sm group-hover:scale-105 transition-transform"
                  />
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                      canApprove ? "bg-red-500 animate-pulse" : "bg-amber-400"
                    }`}
                  />
                </div>

                <div className="flex flex-col min-w-0">
                  <p className="font-black text-gray-800 text-[13px] leading-tight truncate uppercase tracking-tight">
                    {item.employee_name}
                  </p>
                  <p className="text-[10px] font-bold text-gray-400 italic truncate mt-0.5">
                    {item.branch_name}
                  </p>
                </div>
              </div>

              {/* ✅ Giờ OT & Thời gian: Dàn hàng ngang để tiết kiệm chiều cao */}
              <div className="flex items-center justify-between mb-3 pt-3 border-t border-gray-50">
                <span className="px-2 py-0.5 text-[9px] font-black rounded-lg bg-amber-50 text-amber-600 border border-amber-100 uppercase tracking-widest shrink-0">
                  {hours}H OT
                </span>
                <div className="flex items-center text-[11px] font-bold text-gray-500 gap-1.5">
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="text-gray-300 text-[10px]"
                  />
                  {formatDateVN(item.work_date)}
                </div>
              </div>

              {/* ✅ Khung giờ: Thu gọn icon và khoảng cách */}
              <div className="flex items-center text-[11px] font-bold text-gray-600 gap-2 mb-3">
                <div className="w-5 h-5 rounded-md bg-slate-50 flex items-center justify-center text-slate-400">
                  <FontAwesomeIcon icon={faClock} className="text-[9px]" />
                </div>
                <span className="font-black tracking-tighter">
                  {item.ot_from?.slice(0, 5)} — {item.ot_to?.slice(0, 5)}
                </span>
              </div>

              {/* ✅ Lý do: Giảm padding và font size */}
              <div className="p-2.5 bg-gray-50/80 rounded-xl border border-gray-100/50 group-hover:bg-white group-hover:border-red-100 transition-colors">
                <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed italic font-medium">
                  "{item.reason}"
                </p>
              </div>

              {/* ✅ Nút Xóa thu nhỏ */}
              {item.employee_id === currentUserId &&
                item.status === "pending" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id, item.work_date);
                    }}
                    className="absolute bottom-2 right-2 w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                  >
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      className="text-[10px]"
                    />
                  </button>
                )}
            </div>
          );
        })}
      </div>

      {/* =============== POPUP DETAIL NÂNG CẤP =============== */}
      {selected &&
        (() => {
          const canApprove = selected.next_approver_id === currentUserId;

          return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              {/* Overlay mờ */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelected(null)}
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="relative bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden"
              >
                {/* Header Nền trắng sạch sẽ */}
                <div className="p-8 pb-0 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shadow-sm border border-red-100">
                      <Clock size={24} strokeWidth={2.5} />
                    </div>
                    <div>
                      <h2 className="text-xl font-black uppercase tracking-tight text-gray-800">
                        Chi tiết tăng ca
                      </h2>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5 italic">
                        Hệ thống xét duyệt phiếu IMA
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelected(null)}
                    className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-2xl transition-all border border-gray-100"
                  >
                    <X size={20} strokeWidth={2.5} />
                  </button>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                  {/* Info Nhân viên */}
                  <div className="flex items-center gap-5 p-4 bg-slate-50 rounded-3xl border border-slate-100">
                    <img
                      src={selected.employee_avatar || defaultAvatar}
                      className="w-16 h-16 rounded-[1.5rem] object-cover border-2 border-white shadow-md"
                      alt="avatar"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-black text-gray-800 uppercase tracking-tight truncate leading-tight">
                        {selected.employee_name}
                      </h3>
                      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                        {selected.branch_name} • {selected.department_name}
                      </p>

                      {/* STATUS Badge */}
                      <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white border border-slate-100 shadow-sm">
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${
                            selected.status === "pending"
                              ? "bg-amber-400"
                              : "bg-orange-500"
                          }`}
                        />
                        <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                          {selected.status === "pending"
                            ? "Chờ duyệt cấp 1"
                            : "Chờ duyệt cấp 2"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Chi tiết thời gian */}
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      {
                        icon: Calendar,
                        label: "Ngày làm việc",
                        value: formatDateVN(selected.work_date),
                        color: "text-blue-600",
                      },
                      {
                        icon: Clock,
                        label: "Khoảng thời gian",
                        value: `${selected.ot_from?.slice(
                          0,
                          5
                        )} — ${selected.ot_to?.slice(0, 5)}`,
                        color: "text-red-600",
                      },
                      {
                        icon: Timer,
                        label: "Tổng thời lượng",
                        value: `${(selected.ot_minutes / 60).toFixed(2)} giờ`,
                        color: "text-emerald-600",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 bg-white border border-gray-50 rounded-2xl hover:border-gray-200 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2 rounded-xl bg-gray-50 ${item.color}`}
                          >
                            <item.icon size={18} />
                          </div>
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            {item.label}
                          </span>
                        </div>
                        <span className="text-sm font-black text-gray-700">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Lý do */}
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
                      Lý do tăng ca
                    </h4>
                    <div className="p-5 bg-amber-50/30 border border-amber-100/50 rounded-3xl">
                      <p className="text-sm text-amber-900 font-bold italic leading-relaxed">
                        "{selected.reason || "Không có ghi chú cụ thể"}"
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions Button */}
                {canApprove && (
                  <div className="p-8 pt-0 flex flex-col sm:flex-row gap-3">
                    <button
                      className="flex-1 px-6 py-4 border border-rose-100 text-rose-600 rounded-2xl font-black text-[11px] uppercase tracking-widest bg-rose-50 hover:bg-rose-100 transition-all flex items-center justify-center gap-2"
                      onClick={() => {
                        setRejectReason("");
                        setShowRejectPopup(true);
                      }}
                    >
                      <X size={16} strokeWidth={3} /> Từ chối
                    </button>

                    <button
                      className="flex-1 px-10 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] uppercase tracking-[0.15em] rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-100 transition-all active:scale-95"
                      onClick={doApprove}
                    >
                      <CheckCircle2 size={16} strokeWidth={3} /> Phê duyệt phiếu
                    </button>
                  </div>
                )}
              </motion.div>
            </div>
          );
        })()}

{/* =============== POPUP REJECT NÂNG CẤP =============== */}
<AnimatePresence>
  {showRejectPopup && (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      {/* Overlay mờ đồng bộ */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setShowRejectPopup(false)}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white w-full max-w-[400px] rounded-[2rem] shadow-2xl overflow-hidden border border-rose-50"
      >
        {/* Header với Icon cảnh báo */}
        <div className="p-8 pb-4">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shadow-sm">
              <X size={20} strokeWidth={3} />
            </div>
            <h3 className="text-lg font-black uppercase tracking-tight text-gray-800">Lý do từ chối</h3>
          </div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-14">Thông tin bắt buộc để phản hồi nhân viên</p>
        </div>

        {/* Input area */}
        <div className="px-8 pb-6">
          <textarea
            className="w-full bg-slate-50 border border-slate-100 rounded-[1.5rem] p-5 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-rose-100 focus:border-rose-200 focus:bg-white transition-all shadow-inner placeholder:font-medium placeholder:text-gray-300"
            rows={4}
            placeholder="Nhập lý do cụ thể tại sao không duyệt phiếu này..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-gray-50/50 flex gap-3 border-t border-gray-50">
          <button
            onClick={() => setShowRejectPopup(false)}
            className="flex-1 px-4 py-3.5 rounded-2xl font-black text-[11px] text-gray-400 uppercase tracking-widest hover:bg-gray-100 transition-all"
          >
            Quay lại
          </button>
          <button
            onClick={confirmReject}
            className="flex-1 px-4 py-3.5 bg-rose-600 hover:bg-rose-700 text-white font-black text-[11px] uppercase tracking-[0.15em] rounded-2xl shadow-lg shadow-rose-100 transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            <CheckCircle2 size={14} /> Xác nhận
          </button>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>
    </div>
  );
};

export default PendingOvertimeList;
